import { CustomerPortalLogo } from '../components/CustomerPortalLogo';
import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import {
  portalPublicApi,
  type PortalData,
  type PortalOrderSummary,
  type PortalOrderDetail,
  type PortalComplaint,
  type ComplaintAttachment,
  type PortalInvoice,
} from '../lib/publicApi';
import {
  MapPin, RefreshCw, Truck, AlertCircle,
  Loader2, CheckCircle, Edit3, Download,
  ArrowLeft, XCircle, Shield, ChevronDown,
  ImagePlus, Trash2,
} from 'lucide-react';

// ============================================================================
// Helpers
// ============================================================================

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function generateTrackingUrl(carrier: string, trackingNumber: string | null): string | null {
  if (!trackingNumber) return null;
  switch (carrier.toUpperCase()) {
    case 'INPOST': return `https://inpost.pl/sledzenie-przesylek?number=${trackingNumber}`;
    case 'DPD': return `https://tracktrace.dpd.com.pl/parcelDetails?typ=1&p1=${trackingNumber}`;
    case 'DHL': return `https://www.dhl.com/pl-pl/home/sledzenie.html?tracking-id=${trackingNumber}`;
    case 'FEDEX': return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    default: return null;
  }
}

function carrierDisplayName(carrier: string): string {
  switch (carrier.toUpperCase()) {
    case 'INPOST': return 'InPost';
    case 'DPD': return 'DPD';
    case 'DHL': return 'DHL';
    case 'FEDEX': return 'FedEx';
    default: return carrier;
  }
}

function extractError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { data?: { error?: string } } };
    return axiosErr.response?.data?.error || 'Wystąpił błąd';
  }
  return 'Wystąpił błąd';
}

// ============================================================================
// Shipment progress
// ============================================================================

const SHIPMENT_STEPS = [
  { key: 'ordered', label: 'Złożone' },
  { key: 'processing', label: 'Realizacja' },
  { key: 'shipped', label: 'Wysłane' },
  { key: 'in_transit', label: 'W drodze' },
  { key: 'delivered', label: 'Dostarczono' },
];

function getShipmentStepIndex(status: string | null): number {
  if (!status) return 0;
  switch (status.toUpperCase()) {
    case 'CREATED':
    case 'CONFIRMED': return 1;
    case 'DISPATCHED': return 2;
    case 'IN_TRANSIT':
    case 'OUT_FOR_DELIVERY': return 3;
    case 'DELIVERED': return 4;
    default: return 0;
  }
}

function isShipmentTerminal(status: string | null): 'returned' | 'failed' | null {
  if (!status) return null;
  switch (status.toUpperCase()) {
    case 'RETURNED': return 'returned';
    case 'FAILED': return 'failed';
    default: return null;
  }
}

/** Check if resend should be allowed based on shipment status */
function isResendAllowed(shipmentStatus: string | null): boolean {
  if (!shipmentStatus) return false; // no shipment — nothing to resend yet
  switch (shipmentStatus.toUpperCase()) {
    case 'RETURNED':
    case 'FAILED':
      return true;
    default:
      return false; // DELIVERED, IN_TRANSIT, etc. — block
  }
}

// ============================================================================
// Types
// ============================================================================

type PageView = 'loading' | 'orders' | 'address-edit' | 'complaint' | 'inactive' | 'error';

// ============================================================================
// CustomerPortalPage
// ============================================================================

export function CustomerPortalPage() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<PageView>('loading');
  const [portal, setPortal] = useState<PortalData | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);

  const loadPortal = useCallback(async () => {
    if (!token) return;
    try {
      const data = await portalPublicApi.getPortal(token);
      setPortal(data);
      setView('orders');
    } catch (err: unknown) {
      const msg = extractError(err);
      setError(msg);
      setView(msg.includes('dezaktywowany') ? 'inactive' : 'error');
    }
  }, [token]);

  useEffect(() => { loadPortal(); }, [loadPortal]);

  const goBack = () => {
    setView('orders');
    setSelectedOrderId(null);
  };

  const branding = portal?.branding;
  const bgColor = branding?.backgroundColor || '#1a1a2e';
  const accentColor = branding?.accentColor || '#00D4FF';
  const fontColor = branding?.fontColor || '#ffffff';
  const buttonFontColor = branding?.buttonFontColor || '#ffffff';
  const usesLightBranding = ['#f5f5f7', '#ffffff', '#f8fafc'].includes(bgColor.toLowerCase());
  const cardColor = branding?.cardColor || (usesLightBranding ? '#ffffff' : '#20222f');
  const separatorColor = branding?.separatorColor || (usesLightBranding ? '#d7dde6' : '#35384a');
  const buttonColor = branding?.buttonColor || (usesLightBranding ? '#f1f5f9' : '#292c3d');
  const buttonHoverColor = branding?.buttonHoverColor || (usesLightBranding ? '#e2e8f0' : '#34384d');

  return (
    <div
      className="min-h-screen"
      data-portal=""
      style={{
        backgroundColor: bgColor,
        color: fontColor,
        // CSS custom properties for portal-wide usage
        '--portal-card-bg': cardColor,
        '--portal-separator': separatorColor,
        '--portal-button': buttonColor,
        '--portal-button-hover': buttonHoverColor,
        '--portal-accent': accentColor,
        '--portal-font': fontColor,
        '--portal-font-muted': hexToRgba(fontColor, 0.6),
        '--portal-font-dim': hexToRgba(fontColor, 0.3),
        '--portal-input-bg': hexToRgba(fontColor, 0.06),
        '--portal-input-border': hexToRgba(fontColor, 0.15),
        '--portal-input-border-focus': hexToRgba(fontColor, 0.35),
      } as React.CSSProperties}
    >
      {/* Override hardcoded white-opacity Tailwind classes with branding-aware values */}
      <style>{`
        [data-portal] .bg-white\\/10,
        [data-portal] .bg-white\\/5,
        [data-portal] .bg-surface-hover\\/50,
        [data-portal] .bg-surface-hover\\/60,
        [data-portal] .bg-surface-hover\\/70 { background-color: var(--portal-card-bg) !important; }
        [data-portal] .border-white\\/10,
        [data-portal] .border-border,
        [data-portal] .border-border-subtle,
        [data-portal] .border-border-strong { border-color: var(--portal-separator) !important; }
        [data-portal] .border-white\\/20 { border-color: var(--portal-input-border) !important; }
        [data-portal] .text-text-primary { color: var(--portal-font) !important; }
        [data-portal] .text-text-primary\\/60,
        [data-portal] .text-text-primary\\/70 { color: var(--portal-font-muted) !important; }
        [data-portal] .text-text-primary\\/30,
        [data-portal] .text-text-primary\\/20 { color: var(--portal-font-dim) !important; }
        [data-portal] .placeholder-white\\/30::placeholder { color: var(--portal-font-dim) !important; }
        [data-portal] .hover\\:bg-white\\/10:hover,
        [data-portal] .hover\\:bg-white\\/15:hover,
        [data-portal] .hover\\:bg-surface-hover\\/60:hover,
        [data-portal] .hover\\:bg-surface-hover\\/70:hover { background-color: var(--portal-button-hover) !important; }
        [data-portal] .hover\\:border-white\\/40:hover,
        [data-portal] .focus\\:border-white\\/40:focus { border-color: var(--portal-input-border-focus) !important; }
        [data-portal] .border-dashed.border-white\\/20 { border-color: var(--portal-input-border) !important; }
        [data-portal] .bg-black\\/50 { background-color: rgba(0,0,0,0.5) !important; }
        [data-portal] .portal-action-button { background-color: var(--portal-button) !important; }
        [data-portal] .portal-action-button:hover { background-color: var(--portal-button-hover) !important; border-color: var(--portal-accent) !important; }
      `}</style>
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
        <PortalHeader
          branding={branding}
          accentColor={accentColor}
          fontColor={fontColor}
          logoError={logoError}
          onLogoError={() => setLogoError(true)}
          customerName={portal?.customerName}
          showBack={view !== 'orders' && view !== 'loading' && view !== 'error' && view !== 'inactive'}
          onBack={goBack}
        />

        {view === 'loading' && <LoadingState accentColor={accentColor} />}
        {view === 'inactive' && <InactiveState />}
        {view === 'error' && <ErrorState error={error} />}

        {view === 'orders' && portal && token && (
          <OrdersListView
            token={token}
            portal={portal}
            accentColor={accentColor}
            fontColor={fontColor}
            onAddressEdit={(orderId) => { setSelectedOrderId(orderId); setView('address-edit'); }}
            onComplaint={(orderId) => { setSelectedOrderId(orderId); setView('complaint'); }}
          />
        )}

        {view === 'address-edit' && token && selectedOrderId && portal && (
          <AddressEditView
            token={token}
            orderId={selectedOrderId}
            hasTrackingNumber={
              portal.orders.find(o => o.id === selectedOrderId)?.latestShipment?.trackingNumber != null
            }
            accentColor={accentColor}
            fontColor={fontColor}
            onDone={goBack}
          />
        )}

        {view === 'complaint' && token && selectedOrderId && (
          <ComplaintView
            token={token}
            orderId={selectedOrderId}
            accentColor={accentColor}
            fontColor={fontColor}
            buttonFontColor={buttonFontColor}
            storeName={branding?.storeName || ''}
            storeEmail={branding?.storeEmail || null}
            onDone={goBack}
          />
        )}

        <p className="text-xs text-center mt-8" style={{ color: hexToRgba(fontColor, 0.2) }}>
          Powered by EcomFlow
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Portal Header
// ============================================================================

function PortalHeader({ branding, accentColor, fontColor, logoError, onLogoError, customerName, showBack, onBack }: {
  branding: PortalData['branding'] | undefined;
  accentColor: string;
  fontColor: string;
  logoError: boolean;
  onLogoError: () => void;
  customerName: string | null | undefined;
  showBack: boolean;
  onBack: () => void;
}) {
  return (
    <div className="text-center mb-6 sm:mb-8 relative">
      {showBack && (
        <button
          onClick={onBack}
          className="absolute left-0 top-0 p-2 rounded-lg hover:bg-surface-hover/60 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" style={{ color: fontColor, opacity: 0.6 }} />
        </button>
      )}

      {branding?.logoUrl && !logoError ? (
        <CustomerPortalLogo branding={branding} onError={onLogoError} />
      ) : branding?.storeName ? (
        <div
          className="w-12 h-12 rounded-full mx-auto flex items-center justify-center text-xl font-bold"
          style={{ backgroundColor: accentColor, color: fontColor, marginBottom: '40px' }}
        >
          {branding.storeName.charAt(0).toUpperCase()}
        </div>
      ) : null}

      {customerName && (
        <h1 className="text-lg sm:text-xl font-semibold" style={{ color: fontColor }}>
          Cześć, {customerName.split(' ')[0]}!
        </h1>
      )}
      <p className="text-sm mt-1 mx-auto" style={{ color: fontColor, opacity: 0.5, maxWidth: '400px' }}>
        Witaj w Panelu Klienta! W tym miejscu możesz zarządzać wszystkimi najważniejszymi aspektami Twojego zamówienia.
      </p>
    </div>
  );
}

// ============================================================================
// State views
// ============================================================================

function LoadingState({ accentColor }: { accentColor: string }) {
  return (
    <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
      <div className="flex flex-col items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
        <p className="text-text-primary/60 mt-4">Ładowanie panelu...</p>
      </div>
    </div>
  );
}

function InactiveState() {
  return (
    <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
      <div className="flex flex-col items-center py-12">
        <XCircle className="w-12 h-12 text-danger mb-4" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Link dezaktywowany</h2>
        <p className="text-text-primary/60 text-center">
          Ten link do panelu klienta został dezaktywowany. Skontaktuj się ze sklepem.
        </p>
      </div>
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
      <div className="flex flex-col items-center py-12">
        <AlertCircle className="w-12 h-12 text-danger mb-4" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Błąd</h2>
        <p className="text-text-primary/60 text-center">{error}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Orders List View (main view - replaces old HubView)
// ============================================================================

function OrdersListView({ token, portal, accentColor, fontColor, onAddressEdit, onComplaint }: {
  token: string;
  portal: PortalData;
  accentColor: string;
  fontColor: string;
  onAddressEdit: (orderId: string) => void;
  onComplaint: (orderId: string) => void;
}) {
  const sortedOrders = [...portal.orders].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  if (sortedOrders.length === 0) {
    return (
      <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
        <div className="flex flex-col items-center py-8">
          <Truck className="w-12 h-12 text-text-primary/30 mb-4" />
          <h2 className="text-lg font-semibold text-text-primary mb-2">Brak zamówień</h2>
          <p className="text-text-primary/60 text-center text-sm">Nie masz jeszcze żadnych zamówień.</p>
        </div>
      </div>
    );
  }

  const dashedLineStyle = (color: string) => ({
    backgroundImage: `repeating-linear-gradient(to bottom, ${color} 0px, ${color} 4px, transparent 4px, transparent 10px)`,
  });
  const lineColor = portal.branding.separatorColor || hexToRgba(fontColor, 0.18);

  return (
    <div>
      {sortedOrders.map((order, i) => {
        return (
          <div key={order.id}>
            {/* ── Timeline separator ── */}
            <div className="flex flex-col items-center">
              {/* Dashed line above (between previous card and this separator) */}
              {i > 0 && (
                <div className="w-px h-5" style={dashedLineStyle(lineColor)} />
              )}

              {/* Numbered circle above date */}
              <div className="flex flex-col items-center gap-1.5 py-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: hexToRgba(accentColor, 0.15), color: accentColor }}
                >
                  {i + 1}
                </div>
                <span className="text-xs sm:text-sm" style={{ color: hexToRgba(fontColor, 0.55) }}>
                  {new Date(order.createdAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>

              {/* Dashed line below (between separator and card) */}
              <div className="w-px h-4" style={dashedLineStyle(lineColor)} />
            </div>

            {/* ── Order card ── */}
            <OrderCard
              order={order}
              token={token}
              features={portal.features}
              accentColor={accentColor}
              fontColor={fontColor}
              onAddressEdit={() => onAddressEdit(order.id)}
              onComplaint={() => onComplaint(order.id)}
            />
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Order Card - single order with tracking progress and actions
// ============================================================================

function OrderCard({ order, token, features, accentColor, fontColor, onAddressEdit, onComplaint }: {
  order: PortalOrderSummary;
  token: string;
  features: PortalData['features'];
  accentColor: string;
  fontColor: string;
  onAddressEdit: () => void;
  onComplaint: () => void;
}) {
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [invoiceDocuments, setInvoiceDocuments] = useState<PortalInvoice[]>([]);
  const [showInvoiceDocuments, setShowInvoiceDocuments] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  // Blocked message shown after clicking a restricted action (key = button id)
  const [blockedId, setBlockedId] = useState<string | null>(null);
  const [blockedText, setBlockedText] = useState('');

  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(t);
  }, [feedback]);

  useEffect(() => {
    if (!blockedId) return;
    const t = setTimeout(() => { setBlockedId(null); setBlockedText(''); }, 4000);
    return () => clearTimeout(t);
  }, [blockedId]);

  const shipment = order.latestShipment;
  const terminalStatus = shipment ? isShipmentTerminal(shipment.status) : null;
  const activeStep = terminalStatus ? getShipmentStepIndex('IN_TRANSIT') : getShipmentStepIndex(shipment?.status || null);
  const trackingUrl = shipment ? generateTrackingUrl(shipment.carrier, shipment.trackingNumber) : null;
  const resendAllowed = isResendAllowed(shipment?.status || null);
  const hasTrackingNumber = shipment?.trackingNumber != null;

  const downloadInvoiceDocument = async (invoice: PortalInvoice) => {
    setDownloadingInvoiceId(invoice.id);
    try {
      const blob = await portalPublicApi.downloadInvoicePdf(token, order.id, invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const prefix = invoice.type === 'CORRECTION' ? 'korekta' : 'faktura';
      a.download = invoice.invoiceNumber ? `${prefix}-${invoice.invoiceNumber}.pdf` : `${prefix}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setFeedback({ type: 'error', text: 'Nie udało się pobrać dokumentu.' });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleInvoiceDownload = async () => {
    setInvoiceLoading(true);
    setFeedback(null);
    try {
      const invoices = await portalPublicApi.getInvoices(token, order.id);
      const downloadable = invoices.filter(invoice => invoice.hasPdf);
      if (invoices.length === 0) {
        setFeedback({ type: 'info', text: 'Brak faktury do pobrania dla tego zamówienia.' });
        return;
      }
      if (downloadable.length === 0) {
        setFeedback({ type: 'info', text: 'Dokumenty nie są jeszcze gotowe do pobrania.' });
        return;
      }
      if (downloadable.length === 1) {
        await downloadInvoiceDocument(downloadable[0]);
        return;
      }
      setInvoiceDocuments(downloadable);
      setShowInvoiceDocuments(true);
    } catch {
      setFeedback({ type: 'error', text: 'Nie udało się pobrać listy dokumentów.' });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setFeedback(null);
    try {
      const res = await portalPublicApi.initiateResend(token, order.id);
      window.location.href = res.resendLink;
    } catch (err: unknown) {
      setFeedback({ type: 'error', text: extractError(err) });
      setResendLoading(false);
    }
  };

  const showBlocked = (id: string, msg: string) => {
    setBlockedId(id);
    setBlockedText(msg);
  };

  const actionButtons = [
    features.addressEditEnabled && {
      id: 'address',
      icon: <MapPin className="w-4 h-4" />,
      label: 'Zmień dane dostawy',
      description: 'Przydatne, gdy dane w zamówieniu wymagają korekty przed wysyłką.',
      onClick: hasTrackingNumber
        ? () => showBlocked('address', 'Przesyłka została już nadana — zmiana adresu nie jest możliwa.')
        : onAddressEdit,
      loading: false,
    },
    features.resendEnabled && {
      id: 'resend',
      icon: <RefreshCw className="w-4 h-4" />,
      label: 'Ponowna wysyłka',
      description: 'Opcja dostępna w przypadku nieodebranej paczki lub problemów z poprzednią dostawą.',
      onClick: resendAllowed
        ? handleResend
        : () => showBlocked('resend',
            !shipment
              ? 'Przesyłka nie została jeszcze nadana.'
              : 'Opcja niedostępna — ponowne nadanie przesyłki możliwe tylko dla nieodebranych zamówień.'
          ),
      loading: resendLoading,
    },
    features.invoicesEnabled && {
      id: 'invoice',
      icon: <Download className="w-4 h-4" />,
      label: 'Pobierz fakturę',
      description: 'Pobierz fakturę VAT. Jeżeli faktura jest niedostępna - skontaktuj się z obsługą klienta.',
      onClick: handleInvoiceDownload,
      loading: invoiceLoading,
    },
    features.complaintsEnabled && {
      id: 'complaint',
      icon: <AlertCircle className="w-4 h-4" />,
      label: 'Reklamacje i zwroty',
      description: 'Złóż zgłoszenie dotyczące tego zamówienia - zwrot towaru, reklamacja lub wymiana.',
      onClick: order.complaintsAllowed
        ? onComplaint
        : () => showBlocked('complaint', 'Reklamację lub zwrot można zgłosić dopiero po doręczeniu przesyłki.'),
      loading: false,
    },
  ].filter(Boolean) as Array<{ id: string; icon: React.ReactNode; label: string; description: string; onClick: () => void; loading: boolean }>;

  return (
    <div className="backdrop-blur-sm rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--portal-card-bg, rgba(255,255,255,0.1))', border: '1px solid var(--portal-separator, rgba(255,255,255,0.1))' }}>
      {/* Header: order number, date, status, amount */}
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between mb-1">
          <span className="text-base sm:text-lg font-semibold" style={{ color: fontColor }}>
            #{order.orderNumber}
          </span>
        </div>
        <div className="flex items-center justify-between">
          {shipment && shipment.trackingNumber ? (
            <span className="text-xs sm:text-sm truncate" style={{ color: hexToRgba(fontColor, 0.5) }}>
              {carrierDisplayName(shipment.carrier)}
              <span className="mx-1 opacity-50">—</span>
              {trackingUrl ? (
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono underline underline-offset-2 hover:opacity-80 transition-opacity"
                  style={{ color: accentColor }}
                  onClick={e => e.stopPropagation()}
                >
                  {shipment.trackingNumber}
                </a>
              ) : (
                <span className="font-mono">{shipment.trackingNumber}</span>
              )}
            </span>
          ) : (
            <span className="text-xs sm:text-sm" style={{ color: hexToRgba(fontColor, 0.35) }}>
              Brak przesyłki
            </span>
          )}
          <span className="text-sm font-medium flex-shrink-0 ml-3" style={{ color: hexToRgba(fontColor, 0.8) }}>
            {order.totalAmount.toFixed(2)} {order.currency}
          </span>
        </div>
      </div>

      {/* Shipment progress bar */}
      <div className="px-4 sm:px-5 pb-4">
        <ShipmentProgressBar
          activeStep={activeStep}
          terminalStatus={terminalStatus}
          accentColor={accentColor}
          fontColor={fontColor}
        />
        {terminalStatus === 'returned' && (
          <div className="flex items-center gap-2 mt-2">
            <XCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span className="text-orange-400 text-sm font-medium">Przesyłka zwrócona do nadawcy</span>
          </div>
        )}
        {terminalStatus === 'failed' && (
          <div className="flex items-center gap-2 mt-2">
            <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
            <span className="text-danger text-sm font-medium">Błąd dostawy</span>
          </div>
        )}

      </div>

      {/* Action buttons */}
      {actionButtons.length > 0 && (
        <div className="border-t border-border p-4 sm:p-5">
          {/* Mobile: single column so message can appear right after the clicked button */}
          {/* Desktop: 2-col grid, message appears below all buttons */}
          <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2">
            {actionButtons.map((btn) => (
              <Fragment key={btn.id}>
                <button
                  onClick={btn.onClick}
                  disabled={btn.loading}
                  className="portal-action-button flex flex-col gap-1.5 p-3 rounded-lg border border-border transition-all text-left"
                >
                  <div className="flex items-center gap-2">
                    {btn.loading ? (
                      <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: accentColor }} />
                    ) : (
                      <span className="flex-shrink-0" style={{ color: accentColor }}>{btn.icon}</span>
                    )}
                    <span className="text-xs sm:text-sm font-medium leading-tight" style={{ color: hexToRgba(fontColor, 0.85) }}>
                      {btn.label}
                    </span>
                  </div>
                  <span className="text-[11px] sm:text-xs leading-snug" style={{ color: hexToRgba(fontColor, 0.45) }}>
                    {btn.description}
                  </span>
                </button>
                {/* Mobile only: show blocked message right after the button that was clicked */}
                {blockedId === btn.id && blockedText && (
                  <div className="sm:hidden bg-danger/10 border border-red-400/20 rounded-lg px-3 py-2">
                    <p className="text-danger text-xs">{blockedText}</p>
                  </div>
                )}
              </Fragment>
            ))}
          </div>

          {showInvoiceDocuments && invoiceDocuments.length > 0 && (
            <div className="mt-3 rounded-lg border border-border p-3" style={{ backgroundColor: 'var(--portal-card-bg, rgba(255,255,255,0.06))' }}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-medium" style={{ color: hexToRgba(fontColor, 0.85) }}>Dokumenty do pobrania</p>
                <button
                  type="button"
                  onClick={() => setShowInvoiceDocuments(false)}
                  className="text-xs"
                  style={{ color: hexToRgba(fontColor, 0.5) }}
                >
                  Zamknij
                </button>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {invoiceDocuments.map(invoice => (
                  <button
                    key={invoice.id}
                    type="button"
                    onClick={() => void downloadInvoiceDocument(invoice)}
                    disabled={downloadingInvoiceId !== null}
                    className="portal-action-button inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-xs transition-all disabled:opacity-50"
                  >
                    {downloadingInvoiceId === invoice.id
                      ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: accentColor }} />
                      : <Download className="h-4 w-4" style={{ color: accentColor }} />}
                    <span style={{ color: hexToRgba(fontColor, 0.85) }}>
                      {invoice.type === 'CORRECTION' ? 'Korekta' : 'Faktura'}{invoice.invoiceNumber ? ` ${invoice.invoiceNumber}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Desktop: show blocked message below all buttons */}
          {blockedId && blockedText && (
            <div className="hidden sm:block mt-3 bg-danger/10 border border-red-400/20 rounded-lg px-3 py-2">
              <p className="text-danger text-sm">{blockedText}</p>
            </div>
          )}

          {feedback && (
            <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${
              feedback.type === 'error' ? 'bg-danger/10 border border-red-400/20 text-danger' :
              feedback.type === 'success' ? 'bg-success/10 border border-success/20 text-success' :
              'bg-danger/10 border border-red-400/20 text-danger'
            }`}>
              {feedback.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Shipment Progress Bar
// ============================================================================

function ShipmentProgressBar({ activeStep, terminalStatus, accentColor, fontColor }: {
  activeStep: number;
  terminalStatus: 'returned' | 'failed' | null;
  accentColor: string;
  fontColor: string;
}) {
  const isTerminal = terminalStatus === 'returned' || terminalStatus === 'failed';
  const terminalColor = '#f87171'; // red-400

  return (
    <div>
      <div className="flex items-center">
        {SHIPMENT_STEPS.map((step, i) => {
          const isTerminalStep = isTerminal && i === activeStep && step.key === 'in_transit';
          return (
            <Fragment key={step.key}>
              <div
                className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 transition-colors"
                style={{
                  backgroundColor: isTerminalStep
                    ? terminalColor
                    : i <= activeStep ? accentColor : hexToRgba(fontColor, 0.2),
                }}
              />
              {i < SHIPMENT_STEPS.length - 1 && (
                <div
                  className="flex-1 h-0.5"
                  style={{
                    backgroundColor: i < activeStep ? accentColor : hexToRgba(fontColor, 0.12),
                  }}
                />
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5">
        {SHIPMENT_STEPS.map((step, i) => {
          const isTerminalStep = isTerminal && i === activeStep && step.key === 'in_transit';
          return (
            <span
              key={step.key}
              className="text-[10px] sm:text-xs leading-tight"
              style={{
                color: isTerminalStep
                  ? terminalColor
                  : i <= activeStep ? hexToRgba(fontColor, 0.85) : hexToRgba(fontColor, 0.35),
                fontWeight: i === activeStep || isTerminalStep ? 600 : 400,
              }}
            >
              {isTerminalStep ? 'Nieodebrana' : step.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Address Edit View (with shipment tracking check)
// ============================================================================

function AddressEditView({ token, orderId, hasTrackingNumber, accentColor, fontColor, onDone }: {
  token: string;
  orderId: string;
  hasTrackingNumber: boolean;
  accentColor: string;
  fontColor: string;
  onDone: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [address, setAddress] = useState({
    first_name: '', last_name: '', address_1: '', address_2: '',
    city: '', postcode: '', country: 'PL', phone: '',
  });

  useEffect(() => {
    portalPublicApi.getOrder(token, orderId)
      .then((order) => {
        const addr = order.shippingAddress || {};
        setAddress({
          first_name: addr.first_name || '',
          last_name: addr.last_name || '',
          address_1: addr.address_1 || '',
          address_2: addr.address_2 || '',
          city: addr.city || '',
          postcode: addr.postcode || '',
          country: addr.country || 'PL',
          phone: addr.phone || '',
        });
      })
      .catch(() => setError('Nie udało się załadować danych'))
      .finally(() => setLoading(false));
  }, [token, orderId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await portalPublicApi.updateAddress(token, orderId, { shippingAddress: address });
      setSuccess(true);
      setTimeout(onDone, 1500);
    } catch (err: unknown) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState accentColor={accentColor} />;

  if (success) {
    return (
      <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
        <div className="flex flex-col items-center py-8">
          <CheckCircle className="w-12 h-12 mb-4" style={{ color: accentColor }} />
          <h2 className="text-xl font-semibold text-text-primary mb-2">Adres zaktualizowany</h2>
          <p className="text-text-primary/60 text-center">Twój nowy adres dostawy został zapisany.</p>
        </div>
      </div>
    );
  }

  // If shipment already has a tracking number — too late to change address
  if (hasTrackingNumber) {
    return (
      <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
        <div className="flex items-start gap-3 mb-5">
          <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold" style={{ color: fontColor }}>Zmiana adresu niemożliwa</h2>
            <p className="text-sm mt-1" style={{ color: hexToRgba(fontColor, 0.6) }}>
              Przesyłka została już nadana — zmiana adresu dostawy nie jest już możliwa.
              Skontaktuj się ze sklepem, jeśli potrzebujesz pomocy.
            </p>
          </div>
        </div>

        <div className="bg-surface-hover/50 rounded-lg p-4 space-y-1">
          <p className="text-xs font-medium mb-2" style={{ color: hexToRgba(fontColor, 0.4) }}>Aktualny adres dostawy:</p>
          <p className="text-sm" style={{ color: hexToRgba(fontColor, 0.7) }}>
            {address.first_name} {address.last_name}
          </p>
          <p className="text-sm" style={{ color: hexToRgba(fontColor, 0.7) }}>
            {address.address_1}{address.address_2 ? `, ${address.address_2}` : ''}
          </p>
          <p className="text-sm" style={{ color: hexToRgba(fontColor, 0.7) }}>
            {address.postcode} {address.city}
          </p>
          {address.phone && (
            <p className="text-sm" style={{ color: hexToRgba(fontColor, 0.7) }}>
              Tel: {address.phone}
            </p>
          )}
        </div>

        <button
          onClick={onDone}
          className="w-full mt-5 py-3 rounded-xl font-semibold transition-colors bg-surface-hover/60 hover:bg-surface-hover/70"
          style={{ color: fontColor }}
        >
          Wróć do zamówień
        </button>
      </div>
    );
  }

  const inputClass = "w-full bg-surface-hover/60 border border-border-strong rounded-lg px-3 py-2.5 text-text-primary text-sm placeholder-text-muted focus:outline-none focus:border-border-strong";

  return (
    <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
      <div className="flex items-center gap-2 mb-5">
        <Edit3 className="w-5 h-5" style={{ color: accentColor }} />
        <h2 className="text-lg font-semibold" style={{ color: fontColor }}>Zmień adres dostawy</h2>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input value={address.first_name} onChange={e => setAddress(a => ({ ...a, first_name: e.target.value }))} placeholder="Imię" className={inputClass} />
          <input value={address.last_name} onChange={e => setAddress(a => ({ ...a, last_name: e.target.value }))} placeholder="Nazwisko" className={inputClass} />
        </div>
        <input value={address.address_1} onChange={e => setAddress(a => ({ ...a, address_1: e.target.value }))} placeholder="Ulica i numer" className={inputClass} />
        <input value={address.address_2} onChange={e => setAddress(a => ({ ...a, address_2: e.target.value }))} placeholder="Mieszkanie / piętro (opcjonalnie)" className={inputClass} />
        <div className="grid grid-cols-2 gap-3">
          <input value={address.postcode} onChange={e => setAddress(a => ({ ...a, postcode: e.target.value }))} placeholder="Kod pocztowy" className={inputClass} />
          <input value={address.city} onChange={e => setAddress(a => ({ ...a, city: e.target.value }))} placeholder="Miasto" className={inputClass} />
        </div>
        <input value={address.phone} onChange={e => setAddress(a => ({ ...a, phone: e.target.value }))} placeholder="Telefon" className={inputClass} />
      </div>

      {error && (
        <div className="bg-danger/20 border border-red-400/30 rounded-lg px-4 py-3 mt-4">
          <p className="text-danger text-sm">{error}</p>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full mt-5 py-3 rounded-xl text-text-primary font-semibold transition-all disabled:opacity-50"
        style={{ backgroundColor: accentColor }}
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Zapisywanie...
          </span>
        ) : 'Zapisz nowy adres'}
      </button>
    </div>
  );
}

// ============================================================================
// Complaint View
// ============================================================================

// Reason options per complaint type
const RETURN_REASONS = [
  'Nie spełnia oczekiwań',
  'Pomyłka w zamówieniu',
  'Produkt uszkodzony w transporcie',
  'Produkt niezgodny z opisem',
  'Zmiana decyzji',
  'Inne',
];
const COMPLAINT_REASONS = [
  'Wada fabryczna',
  'Uszkodzenie w transporcie',
  'Niezgodność z opisem',
  'Brak elementów',
  'Inne',
];
const EXCHANGE_REASONS = [
  'Niewłaściwy rozmiar',
  'Niewłaściwy kolor',
  'Produkt uszkodzony',
  'Pomyłka w zamówieniu',
  'Inne',
];
const DEFECT_NOTICED_OPTIONS = [
  'Przy odbiorze',
  'W ciągu kilku dni',
  'Po dłuższym użytkowaniu',
];

function ComplaintView({ token, orderId, accentColor, fontColor, buttonFontColor, storeName, storeEmail, onDone }: {
  token: string;
  orderId: string;
  accentColor: string;
  fontColor: string;
  buttonFontColor: string;
  storeName: string;
  storeEmail: string | null;
  onDone: () => void;
}) {
  const [complaints, setComplaints] = useState<PortalComplaint[] | null>(null);
  const [orderDetail, setOrderDetail] = useState<PortalOrderDetail | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [legalExpanded, setLegalExpanded] = useState(false);

  // Form state
  const [activeTab, setActiveTab] = useState<'RETURN' | 'COMPLAINT' | 'EXCHANGE'>('RETURN');
  const [reasonCategory, setReasonCategory] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [defectDescription, setDefectDescription] = useState('');
  const [defectNoticed, setDefectNoticed] = useState('');
  const [photosDescription, setPhotosDescription] = useState('');
  const [exchangeReason, setExchangeReason] = useState('');
  const [desiredProduct, setDesiredProduct] = useState('');
  const [selectedItems, setSelectedItems] = useState<Record<number, boolean>>({});
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);

  useEffect(() => {
    Promise.all([
      portalPublicApi.getComplaints(token, orderId),
      portalPublicApi.getOrder(token, orderId),
    ])
      .then(([complaintsData, orderData]) => {
        setComplaints(complaintsData);
        setOrderDetail(orderData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, orderId]);

  const resetForm = () => {
    setReasonCategory('');
    setShowExplanation(true);
    setExplanation('');
    setDefectDescription('');
    setDefectNoticed('');
    setPhotosDescription('');
    setExchangeReason('');
    setDesiredProduct('');
    setSelectedItems({});
    setBankAccountNumber('');
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => ['image/jpeg', 'image/png', 'image/heic', 'image/heif'].includes(f.type) || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif'));
    if (validFiles.length === 0) return;
    const combined = [...selectedFiles, ...validFiles].slice(0, 5);
    setSelectedFiles(combined);
    // Generate data URL previews (more reliable than blob URLs)
    Promise.all(
      combined.map(f => {
        const isHeic = f.type === 'image/heic' || f.type === 'image/heif' || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif');
        if (isHeic) return Promise.resolve('heic');
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(f);
        });
      })
    ).then(previews => setFilePreviews(previews));
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const getSelectedItemsList = () => {
    if (!orderDetail) return [];
    return orderDetail.items
      .filter((_, i) => selectedItems[i])
      .map(item => ({ name: item.name, quantity: item.quantity, sku: item.sku }));
  };

  const canSubmit = (): boolean => {
    if (activeTab === 'RETURN') {
      if (!reasonCategory) return false;
      if (orderDetail?.isCOD && !/^\d{26}$/.test(bankAccountNumber.replace(/\s/g, ''))) return false;
      return true;
    }
    if (activeTab === 'COMPLAINT') {
      if (!reasonCategory) return false;
      if (!defectDescription.trim()) return false;
      return true;
    }
    if (activeTab === 'EXCHANGE') {
      if (!exchangeReason) return false;
      if (!desiredProduct.trim()) return false;
      return true;
    }
    return false;
  };

  const buildReason = (): string => {
    if (activeTab === 'RETURN') return reasonCategory || 'Zwrot';
    if (activeTab === 'COMPLAINT') return reasonCategory || 'Reklamacja';
    if (activeTab === 'EXCHANGE') return exchangeReason || 'Wymiana';
    return '';
  };

  const buildMetadata = (): Record<string, unknown> => {
    const items = getSelectedItemsList();
    if (activeTab === 'RETURN') {
      return {
        reasonCategory,
        ...(showExplanation && explanation ? { explanation } : {}),
        ...(items.length > 0 ? { selectedItems: items } : {}),
      };
    }
    if (activeTab === 'COMPLAINT') {
      return {
        reasonCategory,
        defectDescription,
        ...(defectNoticed ? { defectNoticed } : {}),
        ...(photosDescription ? { photosDescription } : {}),
        ...(items.length > 0 ? { selectedItems: items } : {}),
      };
    }
    if (activeTab === 'EXCHANGE') {
      return {
        exchangeReason,
        desiredProduct,
        ...(showExplanation && explanation ? { explanation } : {}),
        ...(items.length > 0 ? { selectedItems: items } : {}),
      };
    }
    return {};
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    setError('');
    try {
      // Upload files first if any
      let attachments: ComplaintAttachment[] = [];
      if (selectedFiles.length > 0) {
        const uploadResult = await portalPublicApi.uploadAttachments(token, orderId, selectedFiles);
        attachments = uploadResult.attachments;
      }

      await portalPublicApi.submitComplaint(token, orderId, {
        type: activeTab,
        reason: buildReason(),
        items: getSelectedItemsList(),
        metadata: buildMetadata(),
        bankAccountNumber: (activeTab === 'RETURN' && orderDetail?.isCOD) ? bankAccountNumber.replace(/\s/g, '') : null,
        attachments,
      });
      setSubmitted(true);
      setShowForm(false);
      resetForm();
      const updated = await portalPublicApi.getComplaints(token, orderId);
      setComplaints(updated);
    } catch (err: unknown) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState accentColor={accentColor} />;

  const typeLabels: Record<string, string> = {
    COMPLAINT: 'Reklamacja',
    RETURN: 'Zwrot',
    EXCHANGE: 'Wymiana',
  };

  const statusLabels: Record<string, { label: string; color: string }> = {
    SUBMITTED: { label: 'Złożona', color: '#F59E0B' },
    ACCEPTED: { label: 'Zaakceptowana', color: '#10B981' },
    REJECTED: { label: 'Odrzucona', color: '#EF4444' },
  };

  const tabs = [
    { key: 'RETURN' as const, label: 'Zwrot' },
    { key: 'COMPLAINT' as const, label: 'Reklamacja' },
    { key: 'EXCHANGE' as const, label: 'Wymiana' },
  ];

  const inputClass = 'w-full bg-surface-hover/60 border border-border-strong rounded-lg px-3 py-2.5 text-text-primary text-sm placeholder-text-muted focus:outline-none focus:border-border-strong';
  const selectClass = 'w-full bg-surface-hover/60 border border-border-strong rounded-lg px-3 py-2.5 text-text-primary text-sm focus:outline-none focus:border-border-strong appearance-none';
  const labelClass = 'text-sm mb-1.5 block';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-lg flex items-center gap-2" style={{ color: fontColor }}>
          <AlertCircle className="w-5 h-5" style={{ color: accentColor }} />
          Reklamacje i zwroty
        </h2>
        {!showForm && !submitted && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: accentColor, color: buttonFontColor }}
          >
            Nowe zgłoszenie
          </button>
        )}
      </div>

      {submitted && (
        <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-6">
          <div className="flex flex-col items-center py-4">
            <CheckCircle className="w-10 h-10 mb-3" style={{ color: accentColor }} />
            <h3 className="text-lg font-semibold mb-1" style={{ color: fontColor }}>Zgłoszenie wysłane</h3>
            <p className="text-sm text-center" style={{ color: hexToRgba(fontColor, 0.6) }}>
              Twoje zgłoszenie zostało przyjęte. Skontaktujemy się z Tobą.
            </p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-5">
          {/* Tabs */}
          <div className="flex gap-1 mb-5 bg-surface-hover/50 rounded-lg p-1">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setReasonCategory(''); setExchangeReason(''); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.key ? 'shadow-sm' : 'hover:bg-surface-hover/50'
                }`}
                style={activeTab === tab.key
                  ? { backgroundColor: accentColor, color: buttonFontColor }
                  : { color: hexToRgba(fontColor, 0.6) }
                }
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {/* === RETURN TAB === */}
            {activeTab === 'RETURN' && (
              <>
                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Powód zwrotu *</label>
                  <div className="relative">
                    <select
                      value={reasonCategory}
                      onChange={e => setReasonCategory(e.target.value)}
                      className={selectClass}
                      style={{ color: reasonCategory ? '#fff' : 'rgba(255,255,255,0.3)' }}
                    >
                      <option value="" disabled>Wybierz powód...</option>
                      {RETURN_REASONS.map(r => <option key={r} value={r} style={{ color: '#000' }}>{r}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: hexToRgba(fontColor, 0.4) }} />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showExplanation}
                      onChange={e => setShowExplanation(e.target.checked)}
                      className="w-4 h-4 rounded accent-current"
                      style={{ accentColor }}
                    />
                    <span className="text-sm" style={{ color: hexToRgba(fontColor, 0.7) }}>Chcę dodać dodatkowy opis</span>
                  </label>
                  <p className="text-xs mt-1 ml-6" style={{ color: hexToRgba(fontColor, 0.35) }}>
                    Pole nieobowiązkowe, ale pomoże nam lepiej zrozumieć sytuację i rozwijać firmę.
                  </p>
                </div>

                {showExplanation && (
                  <textarea
                    value={explanation}
                    onChange={e => setExplanation(e.target.value)}
                    placeholder="Opisz, dlaczego chcesz dokonać zwrotu..."
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                )}

                {orderDetail?.isCOD && (
                  <div>
                    <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>
                      Numer konta bankowego (IBAN) *
                    </label>
                    <input
                      type="text"
                      value={bankAccountNumber}
                      onChange={e => setBankAccountNumber(e.target.value.replace(/[^0-9\s]/g, ''))}
                      placeholder="00 0000 0000 0000 0000 0000 0000"
                      maxLength={32}
                      className={inputClass}
                    />
                    <p className="text-xs mt-1" style={{ color: hexToRgba(fontColor, 0.35) }}>
                      Wymagane dla zamówień płatnych za pobraniem. Zwrot zostanie zrealizowany na podane konto.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* === COMPLAINT TAB === */}
            {activeTab === 'COMPLAINT' && (
              <>
                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Powód reklamacji *</label>
                  <div className="relative">
                    <select
                      value={reasonCategory}
                      onChange={e => setReasonCategory(e.target.value)}
                      className={selectClass}
                      style={{ color: reasonCategory ? '#fff' : 'rgba(255,255,255,0.3)' }}
                    >
                      <option value="" disabled>Wybierz powód...</option>
                      {COMPLAINT_REASONS.map(r => <option key={r} value={r} style={{ color: '#000' }}>{r}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: hexToRgba(fontColor, 0.4) }} />
                  </div>
                </div>

                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Opis usterki *</label>
                  <textarea
                    value={defectDescription}
                    onChange={e => setDefectDescription(e.target.value)}
                    placeholder="Opisz szczegółowo usterkę lub wadę produktu..."
                    rows={4}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Kiedy zauważono problem</label>
                  <div className="relative">
                    <select
                      value={defectNoticed}
                      onChange={e => setDefectNoticed(e.target.value)}
                      className={selectClass}
                      style={{ color: defectNoticed ? '#fff' : 'rgba(255,255,255,0.3)' }}
                    >
                      <option value="" style={{ color: '#000' }}>— Wybierz —</option>
                      {DEFECT_NOTICED_OPTIONS.map(o => <option key={o} value={o} style={{ color: '#000' }}>{o}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: hexToRgba(fontColor, 0.4) }} />
                  </div>
                </div>

                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Dodatkowe informacje</label>
                  <textarea
                    value={photosDescription}
                    onChange={e => setPhotosDescription(e.target.value)}
                    placeholder="Dodatkowe uwagi, np. opis wizualny uszkodzenia..."
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-xs mt-1" style={{ color: hexToRgba(fontColor, 0.35) }}>
                    Pole nieobowiązkowe, ale pomoże nam lepiej zrozumieć sytuację i rozwijać firmę.
                  </p>
                </div>
              </>
            )}

            {/* === EXCHANGE TAB === */}
            {activeTab === 'EXCHANGE' && (
              <>
                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Powód wymiany *</label>
                  <div className="relative">
                    <select
                      value={exchangeReason}
                      onChange={e => setExchangeReason(e.target.value)}
                      className={selectClass}
                      style={{ color: exchangeReason ? '#fff' : 'rgba(255,255,255,0.3)' }}
                    >
                      <option value="" disabled>Wybierz powód...</option>
                      {EXCHANGE_REASONS.map(r => <option key={r} value={r} style={{ color: '#000' }}>{r}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: hexToRgba(fontColor, 0.4) }} />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showExplanation}
                      onChange={e => setShowExplanation(e.target.checked)}
                      className="w-4 h-4 rounded accent-current"
                      style={{ accentColor }}
                    />
                    <span className="text-sm" style={{ color: hexToRgba(fontColor, 0.7) }}>Chcę dodać dodatkowy opis</span>
                  </label>
                  <p className="text-xs mt-1 ml-6" style={{ color: hexToRgba(fontColor, 0.35) }}>
                    Pole nieobowiązkowe, ale pomoże nam lepiej zrozumieć sytuację i rozwijać firmę.
                  </p>
                </div>

                {showExplanation && (
                  <textarea
                    value={explanation}
                    onChange={e => setExplanation(e.target.value)}
                    placeholder="Opisz, dlaczego chcesz wymienić produkt..."
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                )}

                <div>
                  <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Na co chcesz wymienić? *</label>
                  <textarea
                    value={desiredProduct}
                    onChange={e => setDesiredProduct(e.target.value)}
                    placeholder="Podaj nazwę, rozmiar, kolor lub wariant produktu, na który chcesz wymienić..."
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </>
            )}

            {/* Photo attachments — common for all tabs */}
            <div>
              <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>
                Zdjęcia (JPG, PNG, HEIC — maks. 5)
              </label>
              <div className="space-y-3">
                {filePreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {filePreviews.map((preview, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden bg-surface-hover/50 aspect-square">
                        {preview && preview !== 'heic' ? (
                          <img src={preview} alt={selectedFiles[i]?.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImagePlus className="w-8 h-8 opacity-40" style={{ color: fontColor }} />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-danger/80 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="w-3 h-3 text-text-primary" />
                        </button>
                        <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-[10px] text-text-primary/70 px-1.5 py-0.5 truncate">
                          {selectedFiles[i]?.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {selectedFiles.length < 5 && (
                  <label className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-border-strong hover:border-border-strong cursor-pointer transition-colors bg-surface-hover/50 hover:bg-surface-hover/60">
                    <ImagePlus className="w-5 h-5 flex-shrink-0" style={{ color: hexToRgba(fontColor, 0.4) }} />
                    <span className="text-sm" style={{ color: hexToRgba(fontColor, 0.5) }}>
                      {selectedFiles.length === 0 ? 'Dodaj zdjęcia' : 'Dodaj więcej zdjęć'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
                      multiple
                      onChange={handleFilesSelected}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-xs mt-1" style={{ color: hexToRgba(fontColor, 0.35) }}>
                Opcjonalnie. Zdjęcia pomogą nam szybciej rozpatrzyć zgłoszenie.
              </p>
            </div>

            {/* Product selection — common for all tabs */}
            {orderDetail && orderDetail.items.length > 0 && (
              <div>
                <label className={labelClass} style={{ color: hexToRgba(fontColor, 0.6) }}>Produkty, których dotyczy zgłoszenie</label>
                <div className="space-y-2">
                  {orderDetail.items.map((item, i) => (
                    <label key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-surface-hover/50 hover:bg-surface-hover/60 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={!!selectedItems[i]}
                        onChange={e => setSelectedItems(prev => ({ ...prev, [i]: e.target.checked }))}
                        className="w-4 h-4 rounded accent-current flex-shrink-0"
                        style={{ accentColor }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate" style={{ color: fontColor }}>{item.name}</p>
                        <p className="text-xs" style={{ color: hexToRgba(fontColor, 0.4) }}>
                          Ilość: {item.quantity}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="bg-danger/20 border border-red-400/30 rounded-lg px-4 py-3">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleSubmit}
                disabled={submitting || !canSubmit()}
                className="flex-1 py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-50"
                style={{ backgroundColor: accentColor, color: buttonFontColor }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Wysyłanie...
                  </span>
                ) : 'Wyślij zgłoszenie'}
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2.5 rounded-lg text-sm bg-surface-hover/50 hover:bg-surface-hover/60 transition-colors"
                style={{ color: hexToRgba(fontColor, 0.6) }}
              >
                Anuluj
              </button>
            </div>
          </div>

          {/* Legal notice */}
          <div className="mt-5 pt-4 border-t border-border">
            <button
              onClick={() => setLegalExpanded(!legalExpanded)}
              className="flex items-center gap-2 w-full text-left"
            >
              <Shield className="w-4 h-4 flex-shrink-0" style={{ color: hexToRgba(fontColor, 0.4) }} />
              <span className="text-xs font-medium flex-1" style={{ color: hexToRgba(fontColor, 0.4) }}>
                Informacja o prawie odstąpienia od umowy
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${legalExpanded ? 'rotate-180' : ''}`}
                style={{ color: hexToRgba(fontColor, 0.3) }}
              />
            </button>

            {legalExpanded && (
              <div className="mt-3 bg-surface-hover/50 rounded-xl p-4 space-y-3 text-xs leading-relaxed" style={{ color: hexToRgba(fontColor, 0.55) }}>
                <p>
                  Zgodnie z obowiązującym prawem, masz prawo odstąpić od umowy zawartej na odległość
                  w ciągu <strong style={{ color: hexToRgba(fontColor, 0.75) }}>14 dni</strong> od otrzymania produktu, bez podania przyczyny.
                </p>

                <div>
                  <p className="font-semibold mb-1" style={{ color: hexToRgba(fontColor, 0.7) }}>Jak skorzystać z prawa odstąpienia?</p>
                  <p>Wypełnij i prześlij powyższy formularz{storeName ? ` do ${storeName}` : ''}{storeEmail ? ` lub wyślij e-mail na adres ${storeEmail}` : ''}.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1" style={{ color: hexToRgba(fontColor, 0.7) }}>Zwrot środków</p>
                  <p>
                    Zwrot należności zostanie zrealizowany w ciągu 14 dni od daty otrzymania przesyłki zwrotnej
                    i weryfikacji stanu zwracanego produktu. Środki są zwracane na ten sam rachunek, z którego
                    dokonano płatności.
                    {orderDetail?.isCOD && (
                      <> W przypadku płatności za pobraniem, konieczne jest podanie numeru konta bankowego w formularzu.</>
                    )}
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1" style={{ color: hexToRgba(fontColor, 0.7) }}>Koszty zwrotu</p>
                  <p>Koszt odesłania produktu ponosi kupujący. Nie podlega on zwrotowi.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1" style={{ color: hexToRgba(fontColor, 0.7) }}>Zmniejszenie wartości produktu</p>
                  <p>
                    W przypadku, gdy produkt nosi ślady użytkowania wykraczające poza niezbędne sprawdzenie
                    (np. uszkodzenia, brak opakowania, ślady użycia), zwracana kwota może zostać pomniejszona
                    proporcjonalnie do utraty wartości.
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1" style={{ color: hexToRgba(fontColor, 0.7) }}>Wadliwy lub uszkodzony produkt?</p>
                  <p>
                    Jeśli produkt jest uszkodzony lub niezgodny z zamówieniem, skorzystaj z zakładki „Reklamacja".
                    Wypełnij opis usterki i odeślij towar. Po otrzymaniu przesyłki produkt zostanie sprawdzony,
                    a środki zwrócone w ustawowym terminie.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Existing complaints - hidden when creating new one */}
      {complaints && complaints.length > 0 && !showForm && !submitted && (
        <div className="space-y-3">
          {complaints.map((c) => {
            const statusInfo = statusLabels[c.status] || { label: c.status, color: '#6B7280' };
            return (
              <div key={c.id} className="bg-surface-hover/60 backdrop-blur-sm rounded-xl border border-border p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium" style={{ color: fontColor }}>{typeLabels[c.type] || c.type}</p>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: statusInfo.color, color: '#ffffff' }}
                  >
                    {statusInfo.label}
                  </span>
                </div>
                <p className="text-sm mb-2" style={{ color: hexToRgba(fontColor, 0.6) }}>{c.reason}</p>
                <p className="text-xs" style={{ color: hexToRgba(fontColor, 0.4) }}>
                  {new Date(c.createdAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                {c.attachments && c.attachments.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {c.attachments.map((att, ai: number) => (
                      att.deleted ? (
                        <div key={ai} className="rounded-lg overflow-hidden bg-surface-hover/50 aspect-square flex items-center justify-center">
                          <div className="text-center p-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mx-auto mb-1 opacity-30" style={{ color: fontColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            <p className="text-[10px] opacity-30" style={{ color: fontColor }}>Usunięte</p>
                          </div>
                        </div>
                      ) : (
                        <a key={ai} href={att.url} target="_blank" rel="noopener noreferrer" className="rounded-lg overflow-hidden bg-surface-hover/50 aspect-square block">
                          <img src={att.url} alt={att.originalName} className="w-full h-full object-cover" />
                        </a>
                      )
                    ))}
                  </div>
                )}
                {c.adminNotes && (
                  <div
                    className="mt-3 rounded-lg p-3 pl-4"
                    style={{
                      backgroundColor: hexToRgba(accentColor, 0.08),
                      borderLeft: `3px solid ${accentColor}`,
                    }}
                  >
                    <p className="text-xs font-medium mb-1" style={{ color: accentColor }}>Odpowiedź sklepu</p>
                    <p className="text-sm" style={{ color: hexToRgba(fontColor, 0.85) }}>{c.adminNotes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {complaints && complaints.length === 0 && !showForm && !submitted && (
        <div className="bg-surface-hover/50 rounded-xl border border-border p-6 text-center">
          <p className="text-sm" style={{ color: hexToRgba(fontColor, 0.5) }}>Nie masz jeszcze żadnych zgłoszeń.</p>
        </div>
      )}

      <button
        onClick={onDone}
        className="w-full py-3 rounded-xl font-semibold transition-colors bg-surface-hover/60 hover:bg-surface-hover/70 mt-2"
        style={{ color: fontColor }}
      >
        Wróć do zamówień
      </button>
    </div>
  );
}
